import classNames from "classnames";
import { useEffect, useState } from "react";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import { LayoutTitleContext } from "../hooks/useLayoutTitle";
import { useTranslation } from "../hooks/useTranslation";
import { MenuIcon } from "./icons/MenuIcon";
import { SearchIcon } from "./icons/SearchIcon";
import { LanguageSwitcher } from "./LanguageSwitcher";
import styles from "./Layout.module.css";
import { Navbar } from "./Navbar";
import { NotificationBell } from "./NotificationBell";
import { UserMenu } from "./UserMenu";

export function Layout() {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [pageTitle, setPageTitle] = useState("");
  const isSearchEnabled =
    location.pathname === "/users" || location.pathname === "/inventory" || location.pathname === "/requests" || location.pathname === "/notifications";
  const currentSearch = isSearchEnabled ? (searchParams.get("search") ?? "") : "";

  useEffect(() => {
    setSearchInput(currentSearch);
  }, [currentSearch]);

  useEffect(() => {
    if (!isSearchEnabled) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const nextSearch = searchInput.trim();
      if (nextSearch === currentSearch) {
        return;
      }

      const nextParams = new URLSearchParams(location.search);
      if (nextSearch) {
        nextParams.set("search", nextSearch);
      } else {
        nextParams.delete("search");
      }
      nextParams.set("page", "1");

      setSearchParams(nextParams, { replace: true });
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [currentSearch, isSearchEnabled, location.search, searchInput, setSearchParams]);

  return (
    <LayoutTitleContext.Provider value={setPageTitle}>
      <div className={styles["root"]}>
        <div className={classNames(styles["sidebarLayer"], isSidebarOpen && styles["sidebarLayerOpen"])}>
          <button
            type="button"
            className={classNames(styles["overlay"], isSidebarOpen && styles["overlayOpen"])}
            onClick={() => setIsSidebarOpen(false)}
            aria-label="Close navigation menu"
          />
          <aside className={classNames(styles["sidebarWrap"], isSidebarOpen && styles["sidebarWrapOpen"])}>
            <Navbar onNavigate={() => setIsSidebarOpen(false)} />
          </aside>
        </div>

        <div className={styles["content"]}>
          <header className={styles["header"]}>
            <div className={styles["headerStart"]}>
              <button
                type="button"
                className={styles["menuButton"]}
                onClick={() => setIsSidebarOpen(true)}
                aria-label="Open navigation menu"
              >
                <MenuIcon className={styles["iconMedium"]} />
              </button>
              {pageTitle && <h1 className={styles["pageTitle"]}>{pageTitle}</h1>}
              {isSearchEnabled && (
                <label className={styles["searchLabel"]}>
                  <SearchIcon className={styles["iconMedium"]} />
                  <input
                    type="search"
                    name="searchInput"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder={t("common.search")}
                    className={styles["searchInput"]}
                    aria-label={t("common.search")}
                  />
                </label>
              )}
            </div>
            <div className={styles["headerActions"]}>
              <NotificationBell />
              <LanguageSwitcher />
              <UserMenu />
            </div>
          </header>

          <main className={styles["main"]}>
            <Outlet />
          </main>
        </div>
      </div>
    </LayoutTitleContext.Provider>
  );
}
